<template>
  <!--
    服务类目维护页 (对应 bidding-task-platform Requirement 18 / 任务 29.1)
    沿用 web/src/views/app/audit/index.vue 的搜索条 + a-table + a-modal 骨架,
    使用 ant-design-vue 1.7.8 风格, 主色 #FF8C00 仅用于 a-tag 与缩略图边框点缀。

    历史「编码 code」与「父类目 parentId」字段已下线, 仅保留 name / sort / isEnabled / iconUrl。
  -->
  <div style="background:#fff; padding:20px;">
    <!-- 顶部搜索区 -->
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <a-form-item label="名称">
          <a-input
            v-model="query.name"
            placeholder="模糊匹配, ≤50 字"
            allow-clear
            :max-length="50"
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="启用状态">
          <a-select
            v-model="query.isEnabled"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option :value="1">启用</a-select-option>
            <a-select-option :value="0">停用</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
          <a-button
            type="primary"
            icon="plus"
            style="margin-left:10px"
            v-permission="'app:service-category:list'"
            @click="handleAdd"
          >新增类目</a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- 列表 -->
    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <!-- 启用状态 -->
      <span slot="isEnabled" slot-scope="text">
        <a-tag v-if="text === 1" color="green">启用</a-tag>
        <a-tag v-else color="red">停用</a-tag>
      </span>

      <!-- 图标缩略图 -->
      <span slot="iconUrl" slot-scope="text">
        <img
          v-if="text"
          :src="text"
          alt="icon"
          style="width:36px; height:36px; object-fit:cover; border:1px solid #FF8C00; border-radius:4px;"
        />
        <span v-else style="color:#999">-</span>
      </span>

      <!-- 操作列 -->
      <span slot="action" slot-scope="text, record">
        <a v-permission="'app:service-category:list'" @click="handleEdit(record)">编辑</a>
        <a-divider type="vertical" />
        <a-popconfirm
          title="确定逻辑删除该服务类目? 不级联删除其下商品与服务项目"
          @confirm="handleDelete(record.id)"
        >
          <a v-permission="'app:service-category:list'" style="color: red">删除</a>
        </a-popconfirm>
      </span>
    </a-table>

    <!-- 新增 / 编辑弹窗 -->
    <a-modal
      v-model="visible"
      :title="form.id ? '编辑服务类目' : '新增服务类目'"
      :confirm-loading="submitting"
      :mask-closable="false"
      width="560px"
      @ok="handleOk"
      @cancel="handleCancel"
    >
      <a-form-model
        ref="formRef"
        :model="form"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 17 }"
      >
        <!-- 名称: 1-50 字符 -->
        <a-form-model-item label="名称" required>
          <a-input
            v-model="form.name"
            :max-length="50"
            placeholder="1~50 字符, 例如 家政保洁"
          />
        </a-form-model-item>

        <!-- 排序: 0-9999, 数值越小越靠前 -->
        <a-form-model-item label="排序" required>
          <a-input-number
            v-model="form.sort"
            :min="0"
            :max="9999"
            style="width:100%"
          />
        </a-form-model-item>

        <!-- 启用状态 -->
        <a-form-model-item label="启用状态" required>
          <a-radio-group v-model="form.isEnabled">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">停用</a-radio>
          </a-radio-group>
        </a-form-model-item>

        <!-- 类目图标: 复用后端统一单图上传接口，保存返回的相对路径 -->
        <a-form-model-item label="类目图标">
          <a-upload
            accept="image/*"
            list-type="picture-card"
            :show-upload-list="false"
            :custom-request="handleIconUpload"
            :before-upload="beforeIconUpload"
            :disabled="iconUploading"
          >
            <img
              v-if="form.iconUrl"
              :src="form.iconUrl"
              alt="类目图标"
              class="icon-upload-image"
            />
            <div v-else>
              <a-icon :type="iconUploading ? 'loading' : 'plus'" />
              <div class="ant-upload-text">{{ iconUploading ? '上传中' : '上传图片' }}</div>
            </div>
          </a-upload>
          <a-button
            v-if="form.iconUrl"
            type="link"
            size="small"
            :disabled="iconUploading"
            class="remove-icon-btn"
            @click="handleRemoveIcon"
          >移除图片</a-button>
          <div class="upload-tip">支持常见图片格式，大小不超过 1MB；点击图片可重新上传。</div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
// 服务类目维护 (Requirement 18.1 ~ 18.7)
// - 列表分页: getServiceCategoryPage, 默认每页 20 条
// - 字段约束: name 1~50 字符, sort 0~9999, isEnabled∈{0,1}, iconUrl≤500
// - 删除: 仅逻辑删除, 不级联子类目与商品
import {
  getServiceCategoryPage,
  createServiceCategory,
  updateServiceCategory,
  deleteServiceCategory,
  uploadCommonFile
} from '@/api'
import { retreatPageAfterDelete } from '@/utils/pagination'

export default {
  name: 'ServiceCategoryView',
  data() {
    return {
      data: [],
      loading: false,
      // 弹窗
      visible: false,
      submitting: false,
      iconUploading: false,
      // 搜索条件
      query: {
        name: '',
        isEnabled: undefined
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      // 表单初值
      form: {
        id: null,
        name: '',
        sort: 0,
        isEnabled: 1,
        iconUrl: ''
      },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '名称', dataIndex: 'name' },
        { title: '排序', dataIndex: 'sort', width: 80 },
        {
          title: '启用状态',
          dataIndex: 'isEnabled',
          width: 100,
          scopedSlots: { customRender: 'isEnabled' }
        },
        {
          title: '图标',
          dataIndex: 'iconUrl',
          width: 90,
          scopedSlots: { customRender: 'iconUrl' }
        },
        { title: '创建时间', dataIndex: 'createdAt', width: 180 },
        {
          title: '操作',
          key: 'action',
          width: 160,
          scopedSlots: { customRender: 'action' }
        }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 加载分页列表
    loadData() {
      this.loading = true
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        name: (this.query.name || '').trim() || undefined,
        isEnabled: this.query.isEnabled
      }
      getServiceCategoryPage(params)
        .then((res) => {
          // 兼容 MyBatis-Plus Page: { records, total } 与 { list, total } 两种结构
          const payload = (res && res.data) || {}
          if (payload.records) {
            this.data = payload.records
            this.pagination.total = payload.total || 0
          } else if (payload.list) {
            this.data = payload.list
            this.pagination.total = payload.total || 0
          } else if (Array.isArray(payload)) {
            this.data = payload
            this.pagination.total = payload.length
          } else {
            this.data = []
            this.pagination.total = 0
          }
        })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    // 查询
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    // 重置
    handleReset() {
      this.query = {
        name: '',
        isEnabled: undefined
      }
      this.pagination.current = 1
      this.loadData()
    },
    // 表格分页
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    // 新增
    handleAdd() {
      this.form = {
        id: null,
        name: '',
        sort: 0,
        isEnabled: 1,
        iconUrl: ''
      }
      this.iconUploading = false
      this.visible = true
    },
    // 编辑
    handleEdit(record) {
      this.form = {
        id: record.id,
        name: record.name,
        sort: record.sort,
        isEnabled: record.isEnabled,
        iconUrl: record.iconUrl || ''
      }
      this.iconUploading = false
      this.visible = true
    },
    // 上传前只允许图片，并与当前服务端默认单文件容量保持一致。
    beforeIconUpload(file) {
      const isImage = file && /^image\//.test(file.type || '')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      const isWithinLimit = file.size / 1024 / 1024 <= 1
      if (!isWithinLimit) {
        this.$message.error('图片大小不能超过 1MB')
        return false
      }
      return true
    },
    // a-upload 自定义请求：复用统一 axios 封装及 /api/common/upload 接口。
    handleIconUpload(options) {
      this.iconUploading = true
      uploadCommonFile(options.file)
        .then((res) => {
          const iconUrl = res && res.data
          if (!iconUrl) throw new Error('上传接口未返回图片地址')
          this.form.iconUrl = iconUrl
          if (typeof options.onSuccess === 'function') options.onSuccess(res)
          this.$message.success('图片上传成功')
        })
        .catch((err) => {
          if (typeof options.onError === 'function') options.onError(err)
          this.$message.error(err && err.message ? err.message : '图片上传失败')
        })
        .finally(() => {
          this.iconUploading = false
        })
    },
    handleRemoveIcon() {
      this.form.iconUrl = ''
    },
    // 关闭弹窗
    handleCancel() {
      if (this.iconUploading) return
      this.visible = false
    },
    // 删除 (逻辑删除)
    handleDelete(id) {
      deleteServiceCategory(id)
        .then(() => {
          this.$message.success('已删除')
          retreatPageAfterDelete(this.pagination, this.data.length)
          this.loadData()
        })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '删除失败')
        })
    },
    // 提交新增 / 修改
    handleOk() {
      if (this.iconUploading) {
        this.$message.warning('图片正在上传，请稍候')
        return
      }
      // 前端轻量校验, 严格校验交由后端 Service 兜底
      const name = (this.form.name || '').trim()
      if (!name) { this.$message.error('请输入名称'); return }
      if (name.length > 50) { this.$message.error('名称长度不能超过 50'); return }
      const sort = Number(this.form.sort)
      if (!Number.isInteger(sort) || sort < 0 || sort > 9999) {
        this.$message.error('排序必须为 0~9999 的整数')
        return
      }
      if (this.form.isEnabled !== 0 && this.form.isEnabled !== 1) {
        this.$message.error('启用状态非法')
        return
      }
      const iconUrl = (this.form.iconUrl || '').trim()
      if (iconUrl.length > 500) {
        this.$message.error('图标地址长度不能超过 500')
        return
      }

      const body = {
        name: name,
        sort: sort,
        isEnabled: this.form.isEnabled,
        iconUrl: iconUrl || null
      }
      this.submitting = true
      const action = this.form.id
        ? updateServiceCategory(this.form.id, body)
        : createServiceCategory(body)
      action
        .then(() => {
          this.$message.success('保存成功')
          this.visible = false
          this.loadData()
        })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '保存失败')
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style scoped>
/* 主色 #FF8C00 仅用于缩略图边框, 其余沿用 ant-design-vue 默认主题 */
.icon-upload-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.remove-icon-btn {
  height: auto;
  padding: 0;
  margin-top: 4px;
  color: #f5222d;
}
.upload-tip {
  color: #999;
  font-size: 12px;
  line-height: 20px;
}
</style>
